import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SectionLabel } from '@/components/site/SectionLabel';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal } from '@/components/site/Reveal';
import { WRAP } from '@/components/site/ServicePageParts';
import { LEGAL } from '@/data/legal';

const OTHER = {
  policy: { to: LEGAL.tos.path, label: 'Terms of Service' },
  tos: { to: LEGAL.policy.path, label: 'Privacy Policy' },
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const pad = (i) => String(i + 1).padStart(2, '0');

/** Tracks which section heading is nearest the top of the viewport. */
function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    setActive(ids[0]);
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    const obs = new IntersectionObserver(
      (entries) => {
        const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (vis[0]) setActive(vis[0].target.id);
      },
      { rootMargin: '-20% 0px -65% 0px' }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [ids]);
  return active;
}

export default function Legal({ doc }) {
  const page = LEGAL[doc];
  const other = OTHER[doc];
  const ids = useMemo(() => page.sections.map((s) => slug(s.title)), [page]);
  const active = useActiveSection(ids);
  const words = page.headline.split(' ');

  return (
    <div data-testid={`${doc}-page`}>
      <section className="relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-[12vw] -top-[18vw] h-[70vw] w-[70vw] max-h-[1000px] max-w-[1000px] rounded-full blur-[20px]"
          style={{ background: 'radial-gradient(circle at 40% 45%, rgba(108,92,231,.28), rgba(75,159,255,.1) 40%, transparent 68%)' }}
        />
        <div className={`relative ${WRAP} pt-28 md:pt-40 pb-16 md:pb-24`}>
          <SectionLabel as="h1" num="01" data-testid={`${doc}-eyebrow`}>
            {page.eyebrow}
          </SectionLabel>
          <WordReveal
            as="p"
            text={page.headline}
            accent={words[words.length - 1]}
            className="mt-8 max-w-[16ch] font-display font-semibold text-[clamp(44px,7.4vw,128px)] leading-[0.92] tracking-[-0.05em]"
          />
          <Reveal delay={350}>
            <div className="mt-12 md:mt-16 pt-6 border-t border-white/[0.08] flex flex-wrap items-center justify-between gap-4">
              <span className="nd-mono">Last updated · <span className="text-white">{page.updated}</span></span>
              <span className="nd-mono">{page.sections.length} sections</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className={`${WRAP} pb-24 md:pb-36`}>
        <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-12 lg:gap-20">
          {/* Table of contents */}
          <aside className="hidden lg:block">
            <nav aria-label="On this page" className="sticky top-32">
              <span className="nd-mono">— On this page</span>
              <ol className="mt-5 border-t border-white/[0.08]">
                {page.sections.map((s, i) => {
                  const id = ids[i];
                  const on = active === id;
                  return (
                    <li key={id} className="border-b border-white/[0.08]">
                      <a
                        href={`#${id}`}
                        aria-current={on ? 'location' : undefined}
                        className={`group flex items-baseline gap-3 py-2.5 text-[13px] leading-snug transition-colors duration-300 ${on ? 'text-white' : 'text-ink-muted hover:text-white'}`}
                      >
                        <span className={`font-mono text-[11px] tabular-nums transition-colors duration-300 ${on ? 'text-[#8C7CFF]' : 'text-ink-muted/70'}`}>{pad(i)}</span>
                        <span className="transition-transform duration-300 [transition-timing-function:var(--ease-out)] group-hover:translate-x-1">{s.title}</span>
                      </a>
                    </li>
                  );
                })}
              </ol>
            </nav>
          </aside>

          {/* Body */}
          <article className="max-w-[68ch]">
            <p className="font-display font-medium text-[clamp(20px,1.8vw,26px)] leading-[1.45] tracking-[-0.02em] text-[#C9C9D3]">{page.intro}</p>

            <div className="mt-14 md:mt-20 border-t border-white/[0.08]">
              {page.sections.map((s, i) => (
                <div key={s.title} id={ids[i]} className="scroll-mt-28 py-10 md:py-14 border-b border-white/[0.08]">
                  <h2 className="font-display text-[26px] md:text-[34px] font-semibold tracking-[-0.035em] leading-[1.1]">
                    <span aria-hidden className="block mb-4 nd-mono tabular-nums">§ {pad(i)}</span>
                    {s.title}
                  </h2>
                  <div className="mt-6 space-y-5 text-[17px] text-[#B4B4C0] leading-[1.75]">
                    {s.body.map((block, j) =>
                      Array.isArray(block) ? (
                        <ul key={j} className="space-y-3 pl-5 list-disc marker:text-violet">
                          {block.map((item) => (
                            <li key={item} className="pl-1">{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p key={j}>{block}</p>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-12 text-[15px] text-ink-muted">
              See also our{' '}
              <Link to={other.to} className="nd-link-u text-white">
                {other.label}
              </Link>
              .
            </p>
          </article>
        </div>
      </section>
    </div>
  );
}
